import React, { useState } from 'react';
import { X, Lightbulb } from 'lucide-react';

/**
 * Dismissible, one-time onboarding banner.
 * State persisted per `id` via localStorage so it only shows on first visit.
 *
 * Usage:
 *   <OnboardingBanner
 *     id="player-wellness-v1"
 *     title="What you can do here"
 *     description="Submit your daily check-in, see 7-day trends, and complete wellness tasks."
 *   />
 */
const OnboardingBanner = ({ id, title = 'What you can do here', description }) => {
  const storageKey = `onboarding_dismissed_${id}`;
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(storageKey) === '1';
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="relative mb-4 p-4 pr-10 rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-gradient-to-r from-emerald-50 to-teal-50/60 dark:from-emerald-950/20 dark:to-teal-950/20"
      data-testid={`onboarding-banner-${id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
          <Lightbulb className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">{title}</p>
          <p className="text-xs text-emerald-800 dark:text-emerald-300 mt-0.5 leading-relaxed">{description}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded-md text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
        aria-label="Dismiss"
        data-testid={`dismiss-banner-${id}`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default OnboardingBanner;
